import React, { useMemo, useRef, useState } from 'react';
import { addVideo } from '../../../../services/videos';
import { uploadImage } from '../../../../services/googleCloud';
import {
  Alert,
  Box,
  Button,
  Card,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../../config/firebaseConfig';
import { uploadMedia } from '../../../../services/media';
import { MediaMetadataFormData } from './StepInformationForm';

interface Props {
  metadata: MediaMetadataFormData;
  mediaType: 'video' | 'audio' | 'pdf';
  onBack: () => void;
  onComplete: () => void;
}

const DEFAULT_MEDIA_THUMBNAIL = '../../assets/images/Portadas/Video-P-1.jpg';

const VideoUpload: React.FC<Props> = ({ metadata, mediaType, onBack, onComplete }) => {
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);
  const hiddenImageInput = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [fileImage, setFileImage] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string>('');

  const mediaLabel = useMemo(() => {
    if (mediaType === 'audio') return 'Audio';
    if (mediaType === 'pdf') return 'PDF';
    return 'Video';
  }, [mediaType]);

  const mediaIcon = useMemo(() => {
    if (mediaType === 'audio') return <AudiotrackOutlinedIcon sx={{ fontSize: 28 }} />;
    if (mediaType === 'pdf') return <PictureAsPdfOutlinedIcon sx={{ fontSize: 28 }} />;
    return <SmartDisplayOutlinedIcon sx={{ fontSize: 28 }} />;
  }, [mediaType]);

  const imagePreview = useMemo(() => {
    if (!fileImage) return '';
    return URL.createObjectURL(fileImage);
  }, [fileImage]);

  const getAcceptedFileTypes = () => {
    if (mediaType === 'audio') return 'audio/*';
    if (mediaType === 'pdf') return '.pdf,application/pdf';
    return 'video/*';
  };

  const getFolderName = () => {
    if (mediaType === 'audio') return 'media/audio';
    if (mediaType === 'pdf') return 'media/pdf';
    return `media/video/${metadata.seriesId || 'general'}`;
  };

  const getCloudFolderName = () => {
    if (mediaType === 'audio') return 'media-audio';
    if (mediaType === 'pdf') return 'media-pdf';
    return metadata.seriesId || 'general';
  };

  const extractMediaUrl = (payload: any): string => {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;

    const candidates = [
      payload.filePath,
      payload.mediaUrl,
      payload.url,
      payload.path,
      payload.downloadUrl,
      payload.gcpUrl,
    ];

    const directMatch = candidates.find((value) => typeof value === 'string' && value.length > 0);
    if (directMatch) return directMatch;

    return (
      extractMediaUrl(payload.data) ||
      extractMediaUrl(payload.media) ||
      extractMediaUrl(payload.result) ||
      extractMediaUrl(payload.response)
    );
  };

  const validateSelectedFile = (selectedFile: File | null) => {
    if (!selectedFile) return false;

    const fileName = selectedFile.name?.toLowerCase() || '';
    const mimeType = selectedFile.type || '';

    if (mediaType === 'audio') return mimeType.startsWith('audio/');
    if (mediaType === 'pdf') return mimeType === 'application/pdf' || fileName.endsWith('.pdf');
    return mimeType.startsWith('video/');
  };

  const uploadMainFile = async (): Promise<string> => {
    if (!file) throw new Error('Selecciona un archivo principal para continuar.');

    if (mediaType !== 'video') {
      const response = await uploadMedia({
        file,
        mediaType,
        folderName: getFolderName(),
        seriesId: metadata.seriesId,
        description: metadata.description,
        tags: metadata.tags,
        allowedSubscriptions: metadata.allowedSubscriptions,
      });

      const uploadedUrl = extractMediaUrl(response?.data);
      if (!uploadedUrl) {
        throw new Error('La API respondió sin URL del archivo cargado.');
      }

      setUploadProgress(100);
      return uploadedUrl;
    }

    return await new Promise<string>((resolve, reject) => {
      const storageRef = ref(storage, `${getFolderName()}/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadProgress(progress);
        },
        (uploadError) => {
          reject(uploadError);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          } catch (finalError) {
            reject(finalError);
          }
        }
      );
    });
  };

  const uploadThumbnail = async () => {
    if (mediaType !== 'video') {
      return DEFAULT_MEDIA_THUMBNAIL;
    }

    if (!fileImage) return '';

    const formData = new FormData();
    formData.append('file', fileImage);
    const response = await uploadImage(getCloudFolderName(), formData);
    return response?.data || '';
  };

  const buildVideoData = (filePath: string, thumbnail: string) => {
    const normalizedTitle = metadata.title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/gi, '')
      .replace(/\s+/g, '');

    const mediaPrefix = mediaType === 'video' ? '' : `${mediaType.toUpperCase()}-`;

    return {
      videoId: `${mediaPrefix}${metadata.seriesId}-${normalizedTitle}`,
      title: metadata.title,
      description: metadata.description,
      tags: metadata.tags,
      duration: metadata.duration,
      intensityLevel: metadata.intensityLevel,
      seriesId: metadata.seriesId,
      seriesEpisodeId: mediaType === 'video' ? metadata.seriesEpisodeId : null,
      subscriptionRequired: true,
      allowedUsers: [],
      allowedSubscriptions: metadata.allowedSubscriptions,
      filePath,
      thumbnail: thumbnail || DEFAULT_MEDIA_THUMBNAIL,
    };
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      setError(null);
      setSuccess('');
      setUploadProgress(5);

      if (!validateSelectedFile(file)) {
        throw new Error(`Selecciona un archivo ${mediaLabel.toUpperCase()} válido.`);
      }

      const uploadedFileUrl = await uploadMainFile();
      const thumbnailUrl = await uploadThumbnail();

      if (mediaType === 'video') {
        const videoData = buildVideoData(uploadedFileUrl, thumbnailUrl);
        await addVideo(videoData);
      }

      setSuccess('Media subida y registrada correctamente.');
      onComplete();
    } catch (uploadError: any) {
      setError(uploadError?.message || 'Error al subir la media.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ color: 'black' }}>
      <div className="mb-5 rounded-xl border border-[#d8ecee] bg-[#f4f6f8] p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e5355' }}>
              Carga de archivo y vista previa
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Revisa la información y sube el archivo final.
            </Typography>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#b0d8dc] bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#2e5355]">
            {mediaIcon}
            {mediaLabel}
          </span>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-[#e5edf0] bg-white p-4 md:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-[#6b7280]">Título</p>
          <p className="font-medium text-[#070707]">{metadata.title || '-'}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-[#6b7280]">Series ID</p>
          <p className="font-medium text-[#070707]">{metadata.seriesId || '-'}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-[#6b7280]">Duración</p>
          <p className="font-medium text-[#070707]">{mediaType === 'video' ? `${metadata.duration || 0}s` : 'Automática'}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-[#6b7280]">Folder</p>
          <p className="font-medium text-[#070707]">{getFolderName()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="!rounded-xl !border !border-[#d8ecee] !shadow-none p-4">
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#2e5355' }}>Archivo principal</Typography>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            className="!w-full !rounded-lg"
            onClick={() => hiddenFileInput.current?.click()}
          >
            Seleccionar {mediaLabel}
          </Button>
          <input
            type="file"
            accept={getAcceptedFileTypes()}
            ref={hiddenFileInput}
            className="hidden"
            onChange={(event) => {
              const selectedFile = event.target.files?.[0] || null;
              setFile(selectedFile);
              setError(null);
            }}
          />

          <div className="mt-3 rounded-lg bg-[#f8fafb] p-3 text-sm text-[#334155]">
            {file ? (
              <>
                <p className="font-medium">{file.name}</p>
                <p className="text-xs text-[#6b7280]">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </>
            ) : (
              <p>Sin archivo seleccionado.</p>
            )}
          </div>
        </Card>

        {mediaType === 'video' ? (
          <Card className="!rounded-xl !border !border-[#d8ecee] !shadow-none p-4">
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#2e5355' }}>Portada (opcional)</Typography>
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              className="!w-full !rounded-lg"
              onClick={() => hiddenImageInput.current?.click()}
            >
              Seleccionar imagen
            </Button>
            <input
              type="file"
              accept="image/*"
              ref={hiddenImageInput}
              className="hidden"
              onChange={(event) => {
                const selectedImage = event.target.files?.[0] || null;
                setFileImage(selectedImage);
              }}
            />

            <div className="mt-3 h-[120px] overflow-hidden rounded-lg bg-[#f8fafb]">
              {imagePreview ? (
                <img src={imagePreview} alt="Vista previa" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-[#94a3b8]">
                  Vista previa de imagen
                </div>
              )}
            </div>
          </Card>
        ) : (
          <Card className="!rounded-xl !border !border-[#d8ecee] !shadow-none p-4">
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#2e5355' }}>Portada</Typography>
            <div className="mt-3 h-[120px] overflow-hidden rounded-lg bg-[#f8fafb]">
              <div className="flex h-full items-center justify-center px-3 text-center text-xs text-[#64748b]">
                Se usará la portada por defecto para {mediaLabel}.
              </div>
            </div>
          </Card>
        )}
      </div>

      {uploading && (
        <div className="mt-4 rounded-lg border border-[#d8ecee] bg-white p-3">
          <div className="mb-2 flex items-center justify-between text-sm text-[#2e5355]">
            <span className="inline-flex items-center gap-1"><CloudUploadOutlinedIcon fontSize="small" /> Subiendo...</span>
            <span>{uploadProgress}%</span>
          </div>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
        </div>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <Button variant="outlined" onClick={onBack} className="!rounded-lg">
          Volver
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleUpload}
          disabled={!file || uploading}
          className="!rounded-lg !px-5"
        >
          Subir y guardar
        </Button>
      </div>
    </Box>
  );
};

export default VideoUpload;
